import icn_linebar_chart from "assets/images/icons/icn_linebar_chart.svg";
import { ReactComponent as Cycle } from "assets/images/icons/icn_table_cell_cycle.svg";
import { ReactComponent as Rankdown } from "assets/images/icons/icn_rank_down.svg";
import { ReactComponent as Rankup } from "assets/images/icons/icn_rank_up.svg";

import React, { useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import { Box, Typography, Tooltip } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";

import useTableInstance from "hooks/useTableInstance";

import CollapseCard from "components/Collapse";
import TableField from "components/Table";
import WiredLinkDegradationDrawerChart from "pages/AInsight/LinkIntegrity/charts/WiredLinkDegradationDrawerChart";

const speedMap = {
  1: null,
  2: 10,
  3: 100,
  4: 1000,
  5: 1000,
  6: 1000,
  7: 2500,
  8: 5000,
  9: 10000,
  11: 40000,
  12: 100000,
};

const formatSpeed = (value) => {
  if (!value || value === 1) return "Auto";

  const speed = speedMap[value];

  if (!speed) return "-";

  return `${speed}M`;
};

const WiredLinkDegradationDrawer = ({
  wiredLinkDegradationApiChartData,
  wiredLinkDegradationChartSeriesData,
  wiredLinkDegradationPortList,
  auth,
}) => {
  const theme = useTheme();
  const columnHelper = useMemo(() => createColumnHelper(), []);

  const speedMismatchData = wiredLinkDegradationPortList?.portList || [];

  const speedMismatchColumns = useMemo(
    () => [
      columnHelper.accessor("deviceName", {
        header: "Device Name",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("ports", {
        header: "Port ID",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("baseSpeed", {
        header: "Baseline",
        cell: (info) => formatSpeed(info.getValue()),
      }),
      columnHelper.accessor("currentSpeed", {
        header: "Current Speed",
        cell: (info) => {
          const currentEnum = info.getValue();
          const baseEnum = info.row.original.baseSpeed;

          const current = speedMap[currentEnum];
          const base = speedMap[baseEnum];

          const isDown = current < base;

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography>{formatSpeed(currentEnum)}</Typography>

              {current && base && (
                <Typography
                  sx={{
                    fontSize: theme.typography.customFontSize[0],
                    color: isDown
                      ? theme.palette.primary.redPrimary
                      : theme.palette.primary.greenPrimary,
                  }}
                >
                  {isDown ? <Rankdown /> : <Rankup />}
                </Typography>
              )}
            </Box>
          );
        },
      }),

      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const handleClick = () => {};

          return (
            <Tooltip title="Retry">
              <Box
                /* onClick={handleClick} */
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  width: 28,
                  height: 28,
                  borderRadius: theme.shape.customBorderRadius[0],
                  transition: "all 0.2s",
                }}
              >
                <Cycle />
              </Box>
            </Tooltip>
          );
        },
      }),
    ],
    [columnHelper],
  );

  const speedMismatchTable = useTableInstance({
    tableName: "speedMismatchDrawerTable",
    columns: speedMismatchColumns,
    data: speedMismatchData,
    enableSorting: false,
    enablePagination: false,
  });

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box
          component="img"
          src={icn_linebar_chart}
          sx={{ width: 20, height: 20, mr: 1.5 }}
        />

        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.customFontSize[3],
          }}
        >
          Wired Link Degradation
        </Typography>
      </Box>

      <CollapseCard title="Wired Congestion">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Box sx={{ width: "100%" }}>
            <WiredLinkDegradationDrawerChart
              data={wiredLinkDegradationChartSeriesData}
            />
          </Box>
          <Box
            sx={{
              border: `1px solid ${theme.palette.primary.greyThread}`,
              borderRadius: theme.shape.customBorderRadius[1],
              px: 2.5,
              py: 1.5,
            }}
          >
            <Typography
              sx={{
                fontSize: theme.typography.customFontSize[1],
                fontWeight: theme.typography.customFontWeight[3],
                color: theme.palette.primary.greyPrimary,
                mb: 0.5,
              }}
            >
              Action Required
            </Typography>

            <Typography
              sx={{
                fontSize: theme.typography.customFontSize[5],
                fontWeight: theme.typography.customFontWeight[3],
                color: theme.palette.primary.main,
                lineHeight: 1.2,
              }}
            >
              Degraded Link
            </Typography>

            <Typography
              sx={{
                fontSize: theme.typography.customFontSize[2],
                color: theme.palette.primary.greyPrimary,
                mt: 0.5,
              }}
            >
              {wiredLinkDegradationApiChartData?.degradedPorts ?? 0} Critical
              Issues
            </Typography>
          </Box>
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                pb: 2,
                borderBottom: `1px solid ${theme.palette.primary.greyThread}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[3],
                  fontWeight: theme.typography.customFontWeight[3],
                  color: theme.palette.primary.main,
                }}
              >
                Speed Mismatch Details
              </Typography>
            </Box>

            <TableField {...speedMismatchTable} />
          </Box>
        </Box>
      </CollapseCard>
    </Box>
  );
};

export default WiredLinkDegradationDrawer;
